import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAppSelector } from '@/store';
import type { TFileAttached } from '@/types/financing';
import type { TDocumentsOnModeration } from '@/types/uploadFiles';

import {
  COLLAPSE_VALID_FILES,
  UNNECESSARY_GUARANTEES_FOR_EURAS
} from '../constants/files.dicts';
import type { FilesStateInterface, FormType } from '../types/form-types';

const getNewestFile = (newFile: TFileAttached, currentFile?: TFileAttached) => {
  const newFileDate = new Date(newFile.create_date).getTime();
  const currentFileDate =
    new Date(currentFile?.create_date || '')?.getTime() || 0;

  return currentFileDate > newFileDate ? currentFile! : newFile;
};

const documentTypeMap: Record<string, string> = {
  SDGZ: 'DGZL',
  SLNB: 'LNBA'
};

const useEurasForm = (documentsOnModeration: TDocumentsOnModeration[]) => {
  const { selectedBank, attachedFiles } = useAppSelector(
    (state) => state.financingStep
  );

  const methods = useForm<FormType>({
    mode: 'all',
    delayError: 200
  });

  const [files, setFiles] = useState<FilesStateInterface>({
    firstStepFiles: [],
    secondStepFiles: [],
    thirdStepFiles: [],
    financedStepFile: []
  });

  const attachedCollapseFiles = useMemo(
    () =>
      attachedFiles?.filter((item, index, self) => {
        if (UNNECESSARY_GUARANTEES_FOR_EURAS.includes(item.type)) return false;

        const isFirstOccurrence =
          self.findIndex(
            (t) => t.name === item.name && t.type === item.type
          ) === index;
        const isValidType = !COLLAPSE_VALID_FILES.includes(item.type);

        return isFirstOccurrence && isValidType;
      }) || [],
    [attachedFiles]
  );

  useEffect(() => {
    const firstStepFilesHandler: Record<string, TFileAttached> = {};
    const secondStepFilesHandler: Record<string, TFileAttached> = {};
    const thirdStepFilesHandler: Record<string, TFileAttached> = {};
    const financedStepFilesHandler: Record<string, TFileAttached> = {};

    const isShowAllDocuments = documentsOnModeration.some(
      (docItem) => docItem.documentType === null
    );

    attachedFiles.forEach((file) => {
      // Выделяем уникальные файлы и выбираем из них новейшие
      if (file.type === 'THSG' || file.type === 'LNGA') {
        firstStepFilesHandler[file.type] = getNewestFile(
          file,
          firstStepFilesHandler[file.type]
        );
      } else if (file.type === 'LGRP' || file.type === 'DOPU') {
        secondStepFilesHandler[file.type] = getNewestFile(
          file,
          secondStepFilesHandler[file.type]
        );
      } else if (
        (file.type === 'LNBA' || file.type === 'DGZL') &&
        (isShowAllDocuments ||
          selectedBank?.status.code !== 'on_rework' ||
          documentsOnModeration.some(
            (docItem) =>
              (documentTypeMap[docItem.documentType] ||
                docItem.documentType) === file.type
          ))
      ) {
        thirdStepFilesHandler[file.type] = getNewestFile(
          file,
          thirdStepFilesHandler[file.type]
        );
      } else if (
        (file.type === 'LGRP' || file.type === 'INSW') &&
        (isShowAllDocuments ||
          selectedBank?.status.code !== 'on_rework' ||
          documentsOnModeration.some(
            (docItem) =>
              (documentTypeMap[docItem.documentType] ||
                docItem.documentType) === file.type
          ))
      ) {
        financedStepFilesHandler[file.type] = getNewestFile(
          file,
          financedStepFilesHandler[file.type]
        );
      }
    });

    setFiles({
      firstStepFiles: Object.values(firstStepFilesHandler),
      secondStepFiles: Object.values(secondStepFilesHandler),
      thirdStepFiles: Object.values(thirdStepFilesHandler),
      financedStepFile: Object.values(financedStepFilesHandler)
    });
  }, [attachedFiles]);

  return { files, methods, attachedCollapseFiles };
};

export default useEurasForm;
